import api from "@/app/api";
import type {
  ProductCreateRequest,
  ProductResponse,
  ProductUpdateRequest,
} from "@/app/api/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { productKeys } from "../keys/products";

/**
 * Create a new product with optimistic updates
 */
export function useCreateProduct(options?: { insertAfterProductId?: string }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProductCreateRequest) => api.products.postProducts(data),

    // Optimistic update for instant UI feedback
    onMutate: async (newProductData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["products", "global"] });

      // Snapshot the previous value
      const previousGlobalCache = queryClient.getQueryData<ProductResponse[]>([
        "products",
        "global",
      ]);

      // Create temporary product for optimistic update
      // Note: units field is excluded from temp data since ProductUnitInput doesn't have the full ProductUnit structure
      // The real data from the API will have the correct units structure
      const { units: _units, ...restData } = newProductData;
      const tempProduct: ProductResponse = {
        id: `temp-${Date.now()}` as any,
        company_id: "temp-company" as any,
        name: restData.name,
        sku: restData.sku,
        code: restData.code,
        short_name: restData.short_name || null,
        piece_weight: restData.piece_weight || null,
        box_weight: restData.box_weight || null,
        weight_price: restData.weight_price || null,
        labels: restData.labels ?? null,
        ai_context: restData.ai_context || null,
        product_metadata: restData.product_metadata || null,
        image_urls: restData.image_urls ?? null,
        is_sleeping: false,
        is_user_product: false,
        units: null, // Will be populated with real data on success
      };

      // Optimistically update global cache - add new product
      queryClient.setQueryData<ProductResponse[]>(
        ["products", "global"],
        (old) => {
          if (!old) return [tempProduct];

          // If insertAfterProductId is specified, place after that product
          if (options?.insertAfterProductId) {
            const insertIndex = old.findIndex(
              (p) => p.id.toString() === options.insertAfterProductId,
            );
            if (insertIndex !== -1) {
              const newArray = [...old];
              newArray.splice(insertIndex + 1, 0, tempProduct);
              return newArray;
            }
          }

          // Default: add at the beginning
          return [tempProduct, ...old];
        },
      );

      return { previousGlobalCache, tempId: tempProduct.id };
    },

    // Success: Replace temp data with real data
    onSuccess: (createdProduct, variables, context) => {
      // Replace temp product with real one in global cache
      queryClient.setQueryData<ProductResponse[]>(
        ["products", "global"],
        (old) => {
          if (!old) return [createdProduct];

          return old.map((p) =>
            p.id === context?.tempId ? createdProduct : p,
          );
        },
      );

      // Set individual product cache
      queryClient.setQueryData(
        productKeys.detail(createdProduct.id),
        createdProduct,
      );
    },

    // Error: Rollback optimistic updates
    onError: (err, variables, context) => {
      if (context?.previousGlobalCache) {
        queryClient.setQueryData(
          ["products", "global"],
          context.previousGlobalCache,
        );
      }
    },

    // Note: No onSettled invalidation - optimistic updates (onMutate + onSuccess)
    // keep the cache consistent without triggering a full 6000+ product reload.
  });
}

/**
 * Update a product with optimistic updates
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProductUpdateRequest }) =>
      api.products.putProductId(
        id as `${string}-${string}-${string}-${string}-${string}`,
        data,
      ),

    onMutate: async ({ id, data }) => {
      // Note: We intentionally don't cancel queries here to avoid UI flickering.
      // The optimistic update will immediately show the new values, and onSuccess
      // will sync with server data. Canceling queries can cause the filtered data
      // to re-compute with stale references, making products disappear temporarily.

      // Snapshot previous values for potential rollback
      const previousProduct = queryClient.getQueryData(productKeys.detail(id));
      const previousGlobalCache = queryClient.getQueryData<ProductResponse[]>([
        "products",
        "global",
      ]);

      // Exclude 'units' from optimistic update - the format is different between
      // ProductUpdateRequest (ProductUnitInput[]) and ProductResponse (ProductUnit[])
      // The server will return the correct format in onSuccess
      const { units: _units, ...dataWithoutUnits } = data;

      // Optimistically update individual product
      queryClient.setQueryData(
        productKeys.detail(id),
        (old: ProductResponse | undefined) => {
          if (!old) return undefined;
          return { ...old, ...dataWithoutUnits };
        },
      );

      // Optimistically update global cache
      queryClient.setQueryData<ProductResponse[]>(
        ["products", "global"],
        (old) => {
          if (!old) return old;
          return old.map((p) => {
            if (p.id.toString() === id) {
              // Create updated product - include null values (they clear fields)
              // Only skip undefined values (not provided in the update)
              const updated = { ...p };
              Object.entries(dataWithoutUnits).forEach(([key, value]) => {
                if (value !== undefined) {
                  (updated as any)[key] = value;
                }
              });
              return updated;
            }
            return p;
          });
        },
      );

      return { previousProduct, previousGlobalCache };
    },

    onSuccess: (updatedProduct: ProductResponse) => {
      // Update caches with real data from server
      // Note: Some computed fields (is_user_product) and relationships (units, when not updated)
      // should be preserved from cache. We merge server data with cached data intelligently.
      queryClient.setQueryData(
        productKeys.detail(updatedProduct.id),
        (old: ProductResponse | undefined) => {
          if (!old) return updatedProduct;
          // Merge: Use server data but preserve certain cached fields
          return {
            ...updatedProduct,
            // Preserve is_user_product from cache since server doesn't return it
            is_user_product: old.is_user_product,
            // Preserve units if server response doesn't include them (defensive)
            // Backend should always return units, but this prevents cache corruption if it doesn't
            units:
              updatedProduct.units !== undefined &&
              updatedProduct.units !== null
                ? updatedProduct.units
                : old.units,
          };
        },
      );

      // Update global cache with the real updated product
      queryClient.setQueryData<ProductResponse[]>(
        ["products", "global"],
        (old) => {
          if (!old) return old;
          return old.map((p) => {
            if (p.id.toString() === updatedProduct.id.toString()) {
              // Merge: Use server data but preserve certain cached fields
              return {
                ...updatedProduct,
                // Preserve is_user_product from cache since server doesn't return it
                is_user_product: p.is_user_product,
                // Preserve units if server response doesn't include them (defensive)
                units:
                  updatedProduct.units !== undefined &&
                  updatedProduct.units !== null
                    ? updatedProduct.units
                    : p.units,
              };
            }
            return p;
          });
        },
      );

      // Note: We intentionally don't invalidate queries here to avoid
      // the product disappearing and reappearing. The optimistic update
      // and onSuccess cache updates are sufficient for data consistency.
    },

    onError: (err, { id }, context) => {
      // Rollback changes
      if (context?.previousProduct) {
        queryClient.setQueryData(
          productKeys.detail(id),
          context.previousProduct,
        );
      }
      if (context?.previousGlobalCache) {
        queryClient.setQueryData(
          ["products", "global"],
          context.previousGlobalCache,
        );
      }
    },
  });
}

/**
 * Delete a product (may result in sleep mode instead)
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.products.deleteProductId(
        id as `${string}-${string}-${string}-${string}-${string}`,
      ),

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: productKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: ["products", "global"] });

      // Snapshot previous values
      const previousProduct = queryClient.getQueryData(productKeys.detail(id));
      const previousGlobalCache = queryClient.getQueryData<ProductResponse[]>([
        "products",
        "global",
      ]);

      // Optimistically remove from global cache
      queryClient.setQueryData<ProductResponse[]>(
        ["products", "global"],
        (old) => {
          if (!old) return old;
          return old.filter((p) => p.id.toString() !== id);
        },
      );

      return { previousProduct, previousGlobalCache };
    },

    onSuccess: (result: { status: string; message: string }, id, context) => {
      if (result.status === "deleted") {
        // Product was actually deleted - remove from all caches
        queryClient.removeQueries({ queryKey: productKeys.detail(id) });
        // Global cache already optimistically updated
      } else if (result.status === "sleeping") {
        // Product was set to sleeping - restore and update in global cache
        if (context?.previousProduct) {
          const sleepingProduct = {
            ...context.previousProduct,
            is_sleeping: true,
          } as ProductResponse;
          queryClient.setQueryData(productKeys.detail(id), sleepingProduct);
          queryClient.setQueryData<ProductResponse[]>(
            ["products", "global"],
            (old) => {
              if (!old) return [sleepingProduct];
              // Restore product but marked as sleeping
              return [...old, sleepingProduct];
            },
          );
        }
        // Invalidate sleeping queries to include this product
        queryClient.invalidateQueries({ queryKey: productKeys.sleeping() });
      }
    },

    onError: (err, id, context) => {
      // Restore previous state
      if (context?.previousProduct) {
        queryClient.setQueryData(
          productKeys.detail(id),
          context.previousProduct,
        );
      }
      if (context?.previousGlobalCache) {
        queryClient.setQueryData(
          ["products", "global"],
          context.previousGlobalCache,
        );
      }
    },
  });
}

/**
 * Sleep a product
 */
export function useSleepProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.products.putProductIdSleep(
        id as `${string}-${string}-${string}-${string}-${string}`,
      ),

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: productKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: productKeys.lists() });

      // Snapshot previous values
      const previousProduct = queryClient.getQueryData(productKeys.detail(id));
      const previousProductLists = queryClient.getQueriesData({
        queryKey: productKeys.lists(),
      });

      // Optimistically update product to sleeping
      queryClient.setQueryData(
        productKeys.detail(id),
        (old: ProductResponse | undefined) =>
          old ? { ...old, is_sleeping: true } : undefined,
      );

      // Optimistically update all lists containing this product
      queryClient.setQueriesData(
        { queryKey: productKeys.lists() },
        (old: any) => {
          if (!old) return old;

          // Handle infinite query structure
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                items:
                  page.items?.map((p: ProductResponse) =>
                    p.id.toString() === id ? { ...p, is_sleeping: true } : p,
                  ) || [],
              })),
            };
          }

          // Fallback for non-infinite query
          if (Array.isArray(old)) {
            return old.map((p) =>
              p.id.toString() === id ? { ...p, is_sleeping: true } : p,
            );
          }

          return old;
        },
      );

      return { previousProduct, previousProductLists };
    },

    onSuccess: (sleepingProduct: ProductResponse) => {
      // Update caches with real data
      queryClient.setQueryData(
        productKeys.detail(sleepingProduct.id),
        sleepingProduct,
      );
      queryClient.setQueriesData(
        { queryKey: productKeys.lists() },
        (old: any) => {
          if (!old) return old;

          // Handle infinite query structure
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                items:
                  page.items?.map((p: ProductResponse) =>
                    p.id.toString() === sleepingProduct.id.toString()
                      ? sleepingProduct
                      : p,
                  ) || [],
              })),
            };
          }

          // Fallback for non-infinite query
          if (Array.isArray(old)) {
            return old.map((p) =>
              p.id.toString() === sleepingProduct.id.toString()
                ? sleepingProduct
                : p,
            );
          }

          return old;
        },
      );

      // Invalidate all product queries to ensure correct filtering
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },

    onError: (err, id, context) => {
      // Rollback changes
      if (context?.previousProduct) {
        queryClient.setQueryData(
          productKeys.detail(id),
          context.previousProduct,
        );
      }
      if (context?.previousProductLists) {
        context.previousProductLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

/**
 * Unsleep (wake) a product
 */
export function useUnsleepProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.products.putProductIdUnsleep(
        id as `${string}-${string}-${string}-${string}-${string}`,
      ),

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: productKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: productKeys.lists() });

      // Snapshot previous values
      const previousProduct = queryClient.getQueryData(productKeys.detail(id));
      const previousProductLists = queryClient.getQueriesData({
        queryKey: productKeys.lists(),
      });

      // Optimistically update product to awake
      queryClient.setQueryData(
        productKeys.detail(id),
        (old: ProductResponse | undefined) =>
          old ? { ...old, is_sleeping: false } : undefined,
      );

      // Optimistically update all lists containing this product
      queryClient.setQueriesData(
        { queryKey: productKeys.lists() },
        (old: any) => {
          if (!old) return old;

          // Handle infinite query structure
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                items:
                  page.items?.map((p: ProductResponse) =>
                    p.id.toString() === id ? { ...p, is_sleeping: false } : p,
                  ) || [],
              })),
            };
          }

          // Fallback for non-infinite query
          if (Array.isArray(old)) {
            return old.map((p) =>
              p.id.toString() === id ? { ...p, is_sleeping: false } : p,
            );
          }

          return old;
        },
      );

      return { previousProduct, previousProductLists };
    },

    onSuccess: (activeProduct: ProductResponse) => {
      // Update caches with real data
      queryClient.setQueryData(
        productKeys.detail(activeProduct.id),
        activeProduct,
      );
      queryClient.setQueriesData(
        { queryKey: productKeys.lists() },
        (old: any) => {
          if (!old) return old;

          // Handle infinite query structure
          if (old.pages) {
            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                items:
                  page.items?.map((p: ProductResponse) =>
                    p.id.toString() === activeProduct.id.toString()
                      ? activeProduct
                      : p,
                  ) || [],
              })),
            };
          }

          // Fallback for non-infinite query
          if (Array.isArray(old)) {
            return old.map((p) =>
              p.id.toString() === activeProduct.id.toString()
                ? activeProduct
                : p,
            );
          }

          return old;
        },
      );

      // Invalidate sleeping products to remove this one
      queryClient.invalidateQueries({ queryKey: productKeys.sleeping() });
    },

    onError: (err, id, context) => {
      // Rollback changes
      if (context?.previousProduct) {
        queryClient.setQueryData(
          productKeys.detail(id),
          context.previousProduct,
        );
      }
      if (context?.previousProductLists) {
        context.previousProductLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

// Note: Product member and import endpoints don't exist in backend
// These would need to be implemented in the backend first

/**
 * Toggle a product in user's list (add if not present, remove if present)
 */
export function useToggleUserProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) =>
      api.products.toggleUserProduct(
        productId as `${string}-${string}-${string}-${string}-${string}`,
      ),

    onMutate: (productId) => {
      // Fast optimistic update - update ALL product queries (including inactive pages)
      // This ensures cache is updated even when switching between products/recap pages
      const queries = queryClient.getQueryCache().findAll({
        queryKey: productKeys.all,
        // REMOVED type: "active" to update all queries, not just rendered ones
      });

      queries.forEach((query) => {
        const old = query.state.data as any;
        if (!old) return;

        let updated = false;
        let newData;

        // Handle infinite query structure
        if (Array.isArray(old.pages)) {
          newData = {
            ...old,
            pages: old.pages.map((page: any) => {
              if (!Array.isArray(page.items)) return page;

              const newItems = page.items.map((p: ProductResponse) => {
                // Check if this is the product to toggle
                if (p.id.toString() === productId) {
                  updated = true;
                  return { ...p, is_user_product: !p.is_user_product };
                }

                // Also check children (sub-products)
                if (p.children && p.children.length > 0) {
                  const updatedChildren = p.children.map((child) => {
                    if (child.id.toString() === productId) {
                      updated = true;
                      return {
                        ...child,
                        is_user_product: !child.is_user_product,
                      };
                    }
                    return child;
                  });

                  // Return parent with updated children if any child was updated
                  if (updated) {
                    return { ...p, children: updatedChildren };
                  }
                }

                return p;
              });

              return updated ? { ...page, items: newItems } : page;
            }),
          };
        }
        // Handle regular query structure
        else if (Array.isArray(old.items)) {
          newData = {
            ...old,
            items: old.items.map((p: ProductResponse) => {
              // Check if this is the product to toggle
              if (p.id.toString() === productId) {
                updated = true;
                return { ...p, is_user_product: !p.is_user_product };
              }

              // Also check children (sub-products)
              if (p.children && p.children.length > 0) {
                const updatedChildren = p.children.map((child) => {
                  if (child.id.toString() === productId) {
                    updated = true;
                    return {
                      ...child,
                      is_user_product: !child.is_user_product,
                    };
                  }
                  return child;
                });

                if (updated) {
                  return { ...p, children: updatedChildren };
                }
              }

              return p;
            }),
          };
        }
        // Fallback for array
        else if (Array.isArray(old)) {
          newData = old.map((p: ProductResponse) => {
            // Check if this is the product to toggle
            if (p.id.toString() === productId) {
              updated = true;
              return { ...p, is_user_product: !p.is_user_product };
            }

            // Also check children (sub-products)
            if (p.children && p.children.length > 0) {
              const updatedChildren = p.children.map((child) => {
                if (child.id.toString() === productId) {
                  updated = true;
                  return { ...child, is_user_product: !child.is_user_product };
                }
                return child;
              });

              if (updated) {
                return { ...p, children: updatedChildren };
              }
            }

            return p;
          });
        }

        // Only update if we found and modified the product
        if (updated && newData) {
          queryClient.setQueryData(query.queryKey, newData);
        }
      });
    },

    onSuccess: (result, productId) => {
      // Fast update with server result - update ALL product queries
      // This ensures cache is updated even when switching between products/recap pages
      const queries = queryClient.getQueryCache().findAll({
        queryKey: productKeys.all,
        // REMOVED type: "active" to update all queries, not just rendered ones
      });

      queries.forEach((query) => {
        const old = query.state.data as any;
        if (!old) return;

        let updated = false;
        let newData;

        // Handle infinite query structure
        if (Array.isArray(old.pages)) {
          newData = {
            ...old,
            pages: old.pages.map((page: any) => {
              if (!Array.isArray(page.items)) return page;

              const newItems = page.items.map((p: ProductResponse) => {
                // Check if this is the product to toggle
                if (p.id.toString() === productId) {
                  updated = true;
                  return { ...p, is_user_product: result.added };
                }

                // Also check children (sub-products)
                if (p.children && p.children.length > 0) {
                  const updatedChildren = p.children.map((child) => {
                    if (child.id.toString() === productId) {
                      updated = true;
                      return { ...child, is_user_product: result.added };
                    }
                    return child;
                  });

                  // Return parent with updated children if any child was updated
                  if (updated) {
                    return { ...p, children: updatedChildren };
                  }
                }

                return p;
              });

              return updated ? { ...page, items: newItems } : page;
            }),
          };
        }
        // Handle regular query structure
        else if (Array.isArray(old.items)) {
          newData = {
            ...old,
            items: old.items.map((p: ProductResponse) => {
              // Check if this is the product to toggle
              if (p.id.toString() === productId) {
                updated = true;
                return { ...p, is_user_product: result.added };
              }

              // Also check children (sub-products)
              if (p.children && p.children.length > 0) {
                const updatedChildren = p.children.map((child) => {
                  if (child.id.toString() === productId) {
                    updated = true;
                    return { ...child, is_user_product: result.added };
                  }
                  return child;
                });

                if (updated) {
                  return { ...p, children: updatedChildren };
                }
              }

              return p;
            }),
          };
        }
        // Fallback for array
        else if (Array.isArray(old)) {
          newData = old.map((p: ProductResponse) => {
            // Check if this is the product to toggle
            if (p.id.toString() === productId) {
              updated = true;
              return { ...p, is_user_product: result.added };
            }

            // Also check children (sub-products)
            if (p.children && p.children.length > 0) {
              const updatedChildren = p.children.map((child) => {
                if (child.id.toString() === productId) {
                  updated = true;
                  return { ...child, is_user_product: result.added };
                }
                return child;
              });

              if (updated) {
                return { ...p, children: updatedChildren };
              }
            }

            return p;
          });
        }

        if (updated && newData) {
          queryClient.setQueryData(query.queryKey, newData);
        }
      });

      // IMPORTANT: Invalidate queries to refetch with updated filters
      // This ensures newly attached products appear in user_products_only queries
      // and detached products are removed from the cache
      queryClient.invalidateQueries({
        queryKey: productKeys.all,
        refetchType: "active", // Only refetch queries that are currently active
      });
    },

    onError: () => {
      // On error, invalidate to refetch and show correct state
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

/**
 * Add a product to user's list
 * Uses optimistic update to avoid refetching all products
 */
export function useAddUserProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) =>
      api.products.addUserProduct(
        productId as `${string}-${string}-${string}-${string}-${string}`,
      ),

    onMutate: async (productId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["products", "global"] });

      // Snapshot the previous value
      const previousProducts = queryClient.getQueryData(["products", "global"]);

      // Optimistically update the product's is_user_product flag
      queryClient.setQueryData(
        ["products", "global"],
        (old: ProductResponse[] | undefined) => {
          if (!old) return old;
          return old.map((p) =>
            p.id === productId ? { ...p, is_user_product: true } : p,
          );
        },
      );

      return { previousProducts };
    },

    onError: (err, productId, context) => {
      // Rollback on error
      if (context?.previousProducts) {
        queryClient.setQueryData(
          ["products", "global"],
          context.previousProducts,
        );
      }
    },

    onSuccess: (result, productId) => {
      // Invalidate user products query to refresh the list
      queryClient.invalidateQueries({ queryKey: ["user-products"] });
      // Invalidate the specific product check
      queryClient.invalidateQueries({
        queryKey: ["user-product-check", productId],
      });
    },
  });
}

/**
 * Remove a product from user's list
 * Uses optimistic update to avoid refetching all products
 */
export function useRemoveUserProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) =>
      api.products.removeUserProduct(
        productId as `${string}-${string}-${string}-${string}-${string}`,
      ),

    onMutate: async (productId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["products", "global"] });

      // Snapshot the previous value
      const previousProducts = queryClient.getQueryData(["products", "global"]);

      // Optimistically update the product's is_user_product flag
      queryClient.setQueryData(
        ["products", "global"],
        (old: ProductResponse[] | undefined) => {
          if (!old) return old;
          return old.map((p) =>
            p.id === productId ? { ...p, is_user_product: false } : p,
          );
        },
      );

      return { previousProducts };
    },

    onError: (err, productId, context) => {
      // Rollback on error
      if (context?.previousProducts) {
        queryClient.setQueryData(
          ["products", "global"],
          context.previousProducts,
        );
      }
    },

    onSuccess: (result, productId) => {
      // Invalidate user products query to refresh the list
      queryClient.invalidateQueries({ queryKey: ["user-products"] });
      // Invalidate the specific product check
      queryClient.invalidateQueries({
        queryKey: ["user-product-check", productId],
      });
    },
  });
}

/**
 * Batch add multiple products to user's list (only adds products not already present)
 */
export function useBatchAddUserProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productIds: string[]) =>
      api.products.batchAddUserProducts(
        productIds as `${string}-${string}-${string}-${string}-${string}`[],
      ),

    onMutate: (productIds) => {
      // Optimistically update all products in the list
      const queries = queryClient.getQueryCache().findAll({
        queryKey: productKeys.all,
      });

      queries.forEach((query) => {
        const old = query.state.data as any;
        if (!old) return;

        let newData;

        // Handle infinite query structure
        if (Array.isArray(old.pages)) {
          newData = {
            ...old,
            pages: old.pages.map((page: any) => {
              if (!Array.isArray(page.items)) return page;

              const newItems = page.items.map((p: ProductResponse) => {
                if (productIds.includes(p.id.toString())) {
                  return { ...p, is_user_product: true };
                }

                // Also check children
                if (p.children && p.children.length > 0) {
                  const updatedChildren = p.children.map((child) => {
                    if (productIds.includes(child.id.toString())) {
                      return { ...child, is_user_product: true };
                    }
                    return child;
                  });

                  return { ...p, children: updatedChildren };
                }

                return p;
              });

              return { ...page, items: newItems };
            }),
          };
        }
        // Handle regular query structure
        else if (Array.isArray(old.items)) {
          newData = {
            ...old,
            items: old.items.map((p: ProductResponse) => {
              if (productIds.includes(p.id.toString())) {
                return { ...p, is_user_product: true };
              }

              if (p.children && p.children.length > 0) {
                const updatedChildren = p.children.map((child) => {
                  if (productIds.includes(child.id.toString())) {
                    return { ...child, is_user_product: true };
                  }
                  return child;
                });

                return { ...p, children: updatedChildren };
              }

              return p;
            }),
          };
        }
        // Fallback for array
        else if (Array.isArray(old)) {
          newData = old.map((p: ProductResponse) => {
            if (productIds.includes(p.id.toString())) {
              return { ...p, is_user_product: true };
            }

            if (p.children && p.children.length > 0) {
              const updatedChildren = p.children.map((child) => {
                if (productIds.includes(child.id.toString())) {
                  return { ...child, is_user_product: true };
                }
                return child;
              });

              return { ...p, children: updatedChildren };
            }

            return p;
          });
        }

        if (newData) {
          queryClient.setQueryData(query.queryKey, newData);
        }
      });
    },

    onSuccess: () => {
      // Invalidate to refetch with updated filters
      queryClient.invalidateQueries({
        queryKey: productKeys.all,
        refetchType: "active",
      });
    },

    onError: () => {
      // On error, invalidate to refetch and show correct state
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

/**
 * Batch remove multiple products from user's list
 */
export function useBatchRemoveUserProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productIds: string[]) => {
      // Call toggle endpoint for each product to remove them
      const promises = productIds.map((id) =>
        api.products.toggleUserProduct(
          id as `${string}-${string}-${string}-${string}-${string}`,
        ),
      );
      return Promise.all(promises);
    },

    onMutate: (productIds) => {
      // Optimistically update all products in the list
      const queries = queryClient.getQueryCache().findAll({
        queryKey: productKeys.all,
      });

      queries.forEach((query) => {
        const old = query.state.data as any;
        if (!old) return;

        let newData;

        // Handle infinite query structure
        if (Array.isArray(old.pages)) {
          newData = {
            ...old,
            pages: old.pages.map((page: any) => {
              if (!Array.isArray(page.items)) return page;

              const newItems = page.items.map((p: ProductResponse) => {
                if (productIds.includes(p.id.toString())) {
                  return { ...p, is_user_product: false };
                }

                // Also check children
                if (p.children && p.children.length > 0) {
                  const updatedChildren = p.children.map((child) => {
                    if (productIds.includes(child.id.toString())) {
                      return { ...child, is_user_product: false };
                    }
                    return child;
                  });

                  return { ...p, children: updatedChildren };
                }

                return p;
              });

              return { ...page, items: newItems };
            }),
          };
        }
        // Handle regular query structure
        else if (Array.isArray(old.items)) {
          newData = {
            ...old,
            items: old.items.map((p: ProductResponse) => {
              if (productIds.includes(p.id.toString())) {
                return { ...p, is_user_product: false };
              }

              if (p.children && p.children.length > 0) {
                const updatedChildren = p.children.map((child) => {
                  if (productIds.includes(child.id.toString())) {
                    return { ...child, is_user_product: false };
                  }
                  return child;
                });

                return { ...p, children: updatedChildren };
              }

              return p;
            }),
          };
        }
        // Fallback for array
        else if (Array.isArray(old)) {
          newData = old.map((p: ProductResponse) => {
            if (productIds.includes(p.id.toString())) {
              return { ...p, is_user_product: false };
            }

            if (p.children && p.children.length > 0) {
              const updatedChildren = p.children.map((child) => {
                if (productIds.includes(child.id.toString())) {
                  return { ...child, is_user_product: false };
                }
                return child;
              });

              return { ...p, children: updatedChildren };
            }

            return p;
          });
        }

        if (newData) {
          queryClient.setQueryData(query.queryKey, newData);
        }
      });
    },

    onSuccess: () => {
      // Invalidate to refetch with updated filters
      queryClient.invalidateQueries({
        queryKey: productKeys.all,
        refetchType: "active",
      });
    },

    onError: () => {
      // On error, invalidate to refetch and show correct state
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}
